import { Box, Paper, Stack } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { contactDefaultValues } from "../../model/constants";
import { IPartnerContactFormValues } from "../../model/types";
import { ContactCriteriaInfo } from "./ContactCriteriaInfo";
import { ContactInfo } from "./ContactInfo";
import { GeneralInfo } from "./GeneralInfo";
import { OtherInfo } from "./OtherInfo";
import { PersonalAccountAccess } from "./PersonalAccountAccess";
import { Statuses } from "./Statuses";

interface IPartnerContactFormProps {
  handleSave: (values: IPartnerContactFormValues) => void;
  contactPerson?: IPartnerContactFormValues;
  hasPartnerProgramConsent?: boolean;
}

export const PartnerContactForm = forwardRef(
  (
    {
      handleSave,
      contactPerson,
      hasPartnerProgramConsent,
    }: IPartnerContactFormProps,
    ref,
  ) => {
    const methods = useForm<IPartnerContactFormValues>({
      defaultValues: contactDefaultValues,
      shouldFocusError: true,
    });
    const { handleSubmit, reset, formState } = methods;

    useImperativeHandle(
      ref,
      () => {
        return {
          submit() {
            return handleSubmit(handleSave)();
          },
        };
      },
      [handleSave, handleSubmit],
    );

    useEffect(() => {
      if (contactPerson && !formState.isDirty) {
        reset(contactPerson);
      }
    }, [contactPerson, reset, formState.isDirty]);

    return (
      <Box
        sx={{
          backgroundColor: (theme) => theme.palette.grey[100],
          px: 4,
          py: 4,
        }}
        component="form"
        onSubmit={handleSubmit(handleSave)}
      >
        <FormProvider {...methods}>
          <Stack spacing={5} sx={{ pt: 2 }}>
            <Box component={Paper} elevation={2} sx={{ p: 2 }}>
              <GeneralInfo />
            </Box>
            <Box component={Paper} elevation={2} sx={{ p: 2 }}>
              <Statuses isMainContact={contactPerson?.is_main_contact} />
            </Box>
            <Box component={Paper} elevation={2} sx={{ p: 2 }}>
              <ContactInfo />
            </Box>
            <Box component={Paper} elevation={2} sx={{ p: 2 }}>
              <PersonalAccountAccess
                hasPartnerProgramConsent={hasPartnerProgramConsent}
              />
            </Box>
            <Box component={Paper} elevation={2} sx={{ p: 2 }}>
              <ContactCriteriaInfo />
            </Box>
            <Box component={Paper} elevation={2} sx={{ p: 2 }}>
              <OtherInfo />
            </Box>
          </Stack>
        </FormProvider>
      </Box>
    );
  },
);

PartnerContactForm.displayName = "PartnerContactForm";
